import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { TestFlowProvider } from './context/TestFlowContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorProvider } from './context/ErrorContext';
import { AIAssistantProvider } from './contexts/AIAssistantContext';
import { ScheduleProvider } from './context/ScheduleContext';
import { SocketProvider } from './context/SocketContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';
import LoadingScreen from './components/common/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';
import { GlobalAIAssistant } from './components/ai/GlobalAIAssistant';
import { GlobalPipelineModal } from './components/common/GlobalPipelineModal';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { OfflinePage } from './components/errors/OfflinePage';
import { NotFoundPage } from './components/errors/NotFoundPage';

import { logger, getErrorMessage } from './utils/logger';
// ⚡ PHASE 3C: Route-Based Code Splitting (Lazy Loading)
// Industry Standard: Lazy load ALL route components to reduce initial bundle size
// Pattern: Next.js, Remix, React Router, Vue Router, Vercel, Netlify
// Benefits: 
//   - Faster initial load (smaller main bundle)
//   - Better performance (only load code when needed)
//   - Smaller bundle chunks (better caching)
//   - Improved Time to Interactive (TTI)
// Reference: https://react.dev/reference/react/lazy

// ✅ Lazy load ALL route components (including auth pages)
// Note: Even small components benefit from code splitting for optimal performance
const ModuleSelection = lazy(() => import('./components/ModuleSelection'));
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const FlowEditor = lazy(() => import('./components/FlowEditor'));
const ScheduledTestsPage = lazy(() => import('./components/ScheduledTestsPage'));
const Reports = lazy(() => import('./components/Reports'));
const ConfigurationPage = lazy(() => import('./components/ConfigurationPage'));
const Settings = lazy(() => import('./components/Settings'));

// Smart redirect component based on auth state
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/modules" replace /> : <Navigate to="/login" replace />;
};

// ✅ Admin-only route component (Configuration Page access control)
// Industry Standard: Role-Based Access Control (RBAC)
// Pattern used by: Django, Spring Security, ASP.NET Core, Laravel
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to dashboard if not admin
  if (user?.role !== 'admin') {
    logger.warn(`[ACCESS DENIED] Non-admin user attempted to access Configuration: ${user?.name}`);
    return <Navigate to="/dashboard" replace />;
  }
  
  // Allow access if admin
  return <>{children}</>;
};

/**
 * AppContent Component
 * 
 * Inner component that can use hooks from providers.
 * Handles offline detection and renders appropriate UI.
 */
function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isOnline = useOnlineStatus();

  // ============================================================================
  // PWA DYNAMIC TITLE UPDATE
  // ============================================================================
  // Sync browser title and iOS home screen name with app settings
  // Updates when user changes app name/initials in Settings → Appearance
  
  useEffect(() => {
    const updateTitle = () => {
      try {
        const appSettings = JSON.parse(localStorage.getItem('appSettings') || '{}');
        const appName = appSettings.appName || 'QA Agent';
        const appLogo = appSettings.appLogo;
        const appInitials = appSettings.appInitials || 'QA';
        
        // Update browser tab title (simple, like Google Chat)
        document.title = appName;
        
        // Update iOS home screen name meta tag
        const iosTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
        if (iosTitle) {
          iosTitle.setAttribute('content', appInitials);
        }
        
        // INDUSTRY STANDARD: Dynamic Favicon with Cache-Busting
        // Pattern: Google Workspace, Slack, ClickUp, Notion
        if (appLogo) {
          // Remove all existing favicon links (forces browser refresh)
          const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
          existingFavicons.forEach(link => link.remove());
          
          // Cache-busting: Add timestamp to force browser to reload icon
          const cacheBuster = `?v=${Date.now()}`;
          const faviconUrl = appLogo + cacheBuster;
          
          // Create new favicon link (primary - Chrome, Firefox, Edge)
          const favicon = document.createElement('link');
          favicon.rel = 'icon';
          favicon.type = 'image/png';
          favicon.href = faviconUrl;
          document.head.appendChild(favicon);
          
          // Create shortcut icon (legacy browser support)
          const shortcutIcon = document.createElement('link');
          shortcutIcon.rel = 'shortcut icon';
          shortcutIcon.href = faviconUrl;
          document.head.appendChild(shortcutIcon);
          
          // Update Apple touch icon (Safari, iOS, macOS home screen)
          const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
          if (appleTouchIcon) {
            appleTouchIcon.setAttribute('href', faviconUrl);
          } else {
            const newAppleIcon = document.createElement('link');
            newAppleIcon.rel = 'apple-touch-icon';
            newAppleIcon.href = faviconUrl;
            document.head.appendChild(newAppleIcon);
          }
          
          logger.debug(`[Browser] Favicon updated: ${appLogo}`);
        }
        
        logger.debug(`� [Browser] Tab title: "${appName}"`);
      } catch (error) {
        logger.error('[Browser] Failed to update tab icon/title:', { error: getErrorMessage(error) });
      }
    };
    
    // Update on mount
    updateTitle();
    
    // Listen for settings changes from Appearance settings page
    window.addEventListener('appSettingsUpdated', updateTitle);
    
    return () => {
      window.removeEventListener('appSettingsUpdated', updateTitle);
    };
  }, []);

  // Show offline page if no internet connection
  if (!isOnline) {
    return <OfflinePage />;
  }

  return (
    <>
      {/* ✅ WCAG 2.1: Skip to Main Content Link - Accessibility Enhancement */}
      {/* Industry Standard: Screen reader & keyboard navigation best practice */}
      {/* Pattern: GitHub, BBC, GOV.UK, W3C */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[10000] focus:px-6 focus:py-3 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-800 focus:font-semibold focus:text-sm focus:transition-all"
      >
        Skip to main content
      </a>

      <Routes>
                  {/* Root redirect - smart routing based on auth state */}
                  <Route path="/" element={<RootRedirect />} />
                  
                  {/* Public Routes - Suspense required for lazy-loaded components */}
                  <Route path="/login" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><Login /></Suspense>} />
                  <Route path="/signup" element={<Suspense fallback={<LoadingScreen message="Loading..." />}><Signup /></Suspense>} />
                  
                  {/* Module Selection - Protected (requires auth + password reset check) */}
                  <Route path="/modules" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingScreen message="Loading modules..." />}>
                        <ModuleSelection />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  
                  {/* Global Settings - Protected but standalone (no sidebar) */}
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingScreen message="Loading Settings..." />}>
                        <Settings />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  
                  {/* Password Reset - Protected but standalone (no sidebar) */}
                  <Route path="/reset-password" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LoadingScreen message="Loading..." />}>
                        <PasswordReset />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  
                  {/* Automation Module Protected Routes (with sidebar) */}
                  <Route path="/*" element={
                    <ProtectedRoute>
                        <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
                          <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

                          {/* Fixed margin - sidebar expands as overlay */}
                          <div className="ml-16">
                            {/* ✅ WCAG 2.1: Semantic Main Landmark with ARIA */}
                            {/* Industry Standard: Clearly defined main content region */}
                            {/* Pattern: ARIA Authoring Practices Guide (APG) */}
                            <main 
                              id="main-content" 
                              role="main" 
                              className="p-6"
                              aria-label="Main content"
                            >
                              {/* ⚡ Suspense Wrapper: Shows loading screen while code chunks load */}
                              <Suspense fallback={<LoadingScreen message="Loading page..." />}>
                                <Routes>
                                  <Route path="/dashboard" element={<Dashboard />} />
                                  <Route path="/flows" element={<FlowEditor />} />
                                  <Route path="/scheduled-tests" element={<ScheduledTestsPage />} />
                                  <Route path="/reports" element={<Reports />} />
                                  <Route path="/configuration" element={
                                    <AdminRoute>
                                      <ConfigurationPage />
                                    </AdminRoute>
                                  } />
                                  {/* 404 Catch-all for automation module routes */}
                                  <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                              </Suspense>
                            </main>
                          </div>
                        </div>
                    </ProtectedRoute>
                  } />
                </Routes>
                
      {/* Global AI Assistant Modal */}
      <GlobalAIAssistant />
      
      {/* Global Pipeline Progress Modal (Multi-user safe, operation-isolated) */}
      <GlobalPipelineModal />
    </>
  );
}

/**
 * App Component (Root)
 * 
 * Root application component with complete provider hierarchy.
 * 
 * Provider Order (CRITICAL - Do not change):
 * 1. ErrorBoundary - Catches all React errors
 * 2. ThemeProvider - Theme needed by all components
 * 3. ErrorProvider - Global error state (NEW - Phase 2)
 * 4. ToastProvider - Toast notifications
 * 5. AIAssistantProvider - AI assistant state
 * 6. Router - Routing context
 * 7. AuthProvider - Authentication state
 * 8. SocketProvider - Single shared Socket.IO connection
 * 9. TestFlowProvider - Test flow state (uses shared socket)
 * 
 * @returns {JSX.Element} Root app component
 */
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ErrorProvider>
          <ToastProvider>
            <AIAssistantProvider>
              <Router>
                <AuthProvider>
                  <SocketProvider>
                    <TestFlowProvider>
                      <ScheduleProvider>
                        <AppContent />
                      </ScheduleProvider>
                    </TestFlowProvider>
                  </SocketProvider>
                </AuthProvider>
              </Router>
            </AIAssistantProvider>
          </ToastProvider>
        </ErrorProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
